import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Modal, Popover } from 'antd';
import { ExclamationCircleFilled } from '@ant-design/icons';
import { Article, Cube, Info, Scroll, Sparkle } from '@phosphor-icons/react';
import dayjs from 'dayjs';
import { useOneButtonStore } from '../../../../store/oneButtonStore';
import quoteServices from '../../../../services/oneButtonServices/quoteServices';
import opportunityInfoServices from '../../../../services/oneButtonServices/opportunityInfoService';
import {
    changeOrderPartnerLabels,
    computeCommerceGuide,
    hasPendingChangeOrderFlag,
} from './commerceGuideLogic';
import { getOneButtonCapabilityValue } from '../../../../services/oneButtonServices/capabilityService';
import DashboardModal from './DashboardModal';
import { isDashboardCommerceReady } from './systemDesignMissingItems';
import { useSystemDesignMissingItems } from './useSystemDesignMissingItems';
import DesignLossesSourceBadge from './DesignLossesSourceBadge';
import './oneButton-css/DashboardAgentFooter.css';

export const DashboardAgentFooter: React.FC = () => {
    const theme = useOneButtonStore((s) => s.theme);
    const opportunityID = useOneButtonStore((s) => s.opportunityID);
    const selectedFinanceCells = useOneButtonStore((s) => s.selectedFinanceCells);
    const financePartnerSyncStatus = useOneButtonStore((s) => s.financePartnerSyncStatus);
    const opportunityDetails = useOneButtonStore((s) => s.opportunityDetails);
    const dashboardQuotesSummaryLines = useOneButtonStore((s) => s.dashboardQuotesSummaryLines);
    const changeOrderDetails = useOneButtonStore((s) => s.changeOrderDetails);
    const checkListDetails = useOneButtonStore((s) => s.checkListDetails);
    // const contractChecklistPageChanges = useOneButtonStore((s) => s.contractChecklistPageChanges);
    const setProductsOpen = useOneButtonStore((s) => s.setCommerceHubProductsOpen);
    const setQuotesOpen = useOneButtonStore((s) => s.setCommerceHubQuotesOpen);
    const setAgreementsOpen = useOneButtonStore((s) => s.setCommerceHubAgreementsOpen);
    const onebuttonCapabilities = useOneButtonStore((s) => s.onebuttonCapabilities);
    const systemDesignLosses = useOneButtonStore((s) => s.systemDesignLosses);

    const generateContractAccess = getOneButtonCapabilityValue(onebuttonCapabilities, 'Generate_Contract_Access');
    const hideAgreements = generateContractAccess !== 'Edit' && generateContractAccess !== 'View';
    const systemDesignMissingItems = useSystemDesignMissingItems();
    const commerceReady = isDashboardCommerceReady(systemDesignMissingItems);

    const openCommerceShortcut = (kind: 'products' | 'quotes' | 'agreements') => {
        if (!commerceReady) {
            const first = systemDesignMissingItems.find((i) => i.severity === 'hard');
            Modal.warning({
                title: 'Complete required steps first',
                content: first
                    ? `Finish “${first.label}” (and remaining hard misses) before opening ${kind}.`
                    : `Complete customer, utility, and product designs before opening ${kind}.`,
                okText: 'OK',
                centered: true,
                zIndex: 14000,
                className: `obd-theme-${theme}`,
                rootClassName: `obd-theme-${theme}`,
            });
            return;
        }
        if (kind === 'products') setProductsOpen(true);
        else if (kind === 'quotes') setQuotesOpen(true);
        else setAgreementsOpen(true);
    };

    const [changeOrderShow, setChangeOrderShow] = useState<boolean>(false);
    const [isChangeOrderModalOpen, setIsChangeOrderModalOpen] = useState(false);
    const [selectedPartnerDetail, setSelectedPartnerDetail] = useState<any>(null);
    const [isChecklistModalOpen, setIsChecklistModalOpen] = useState<boolean>(false);
    const [checklistModalType, setChecklistModalType] = useState<'change-order' | 'check-List'>('check-List');
    // const [checkListDetails, setCheckListDetails] = useState<any[]>([]);
    const firstLoadLoanStatusByOppRef = useRef<string | null>(null);

    const numericOpportunityId = useMemo(() => {
        const raw = opportunityID ?? opportunityDetails?.Opportunity_Id ?? opportunityDetails?.OpportunityId ?? null;
        if (raw == null) return null;
        const v = String(raw).trim();
        return v !== '' ? v : null;
    }, [opportunityID, opportunityDetails]);

    useEffect(() => {
        if (!numericOpportunityId) return;
        if (firstLoadLoanStatusByOppRef.current === numericOpportunityId) return;
        // First footer load per opportunity: hydrate change-order rows from Contract `getLoanStatus`.
        firstLoadLoanStatusByOppRef.current = numericOpportunityId;
        void quoteServices.contractGetLoanStatusByOpportunity(numericOpportunityId).catch(() => {
            // Angular parity: this fetch is non-blocking and silent on footer init.
        });
        void quoteServices
            .getPageChanges(numericOpportunityId)
            .then((res) => {
                const rows = Array.isArray(res)
                    ? res
                    : res != null && typeof res === 'object'
                    ? [res]
                    : [];
                // setCheckListDetails(rows);
            })
            .catch(() => {
                // Angular parity: checklist fetch is non-blocking for footer render.
                // setCheckListDetails([]);
            });
    }, [numericOpportunityId]);

    const agentMessage = useMemo(
        () =>
            computeCommerceGuide({
                selectedFinanceCells,
                financePartnerSyncStatus,
                opportunityDetails,
                dashboardQuotesSummaryLines,
                changeOrderDetails,
            }).message,
        [
            selectedFinanceCells,
            financePartnerSyncStatus,
            opportunityDetails,
            dashboardQuotesSummaryLines?.selectedQuoteLine,
            dashboardQuotesSummaryLines?.signedContractLine,
            changeOrderDetails,
        ]
    );

    const [enableContractCancellationRequired, setEnableContractCancellationRequired] = useState<boolean>(false);

    useEffect(() => {
        let status: boolean = false;
        const hasLightReachSolarFinancePartner =opportunityDetails?.OpportunityProduct.find(
            (product: any) =>
              product.Product_Type_Name === 'Solar' &&
              product.Finance_Partner.toLowerCase() === "lightreach"
        );
        const hasGoodLeapSolarFinancePartner =opportunityDetails?.OpportunityProduct.find(
        (product: any) =>
            product.Product_Type_Name === 'Solar' &&
            product.Finance_Partner.toLowerCase() === "goodleap"
        );
      
        if (Array.isArray(changeOrderDetails)) {
            const goodLeapData = changeOrderDetails.find(
              (x: any) => x.Finance_Partner_Name.toLowerCase() == "goodleap"
            );
            const lightReachData = changeOrderDetails.find(
              (x: any) => x.Finance_Partner_Name.toLowerCase() == "lightreach"
            );
            if (goodLeapData && hasGoodLeapSolarFinancePartner) {
                status = !!goodLeapData.Is_Change_Order;
            }
      
            if (lightReachData && hasLightReachSolarFinancePartner) {
                status =
                    lightReachData?.application_Status === 'Sent' ||
                    lightReachData?.application_Status === 'Signed' ||
                    lightReachData?.application_Status === 'Generated'
            }
        }
        setEnableContractCancellationRequired(status);

    },[changeOrderDetails,opportunityDetails])
    


    useEffect(() => {
        if (!changeOrderDetails) return;
        setChangeOrderShow(hasPendingChangeOrderFlag(changeOrderDetails));
    },[changeOrderDetails])

    const prevEnfinStatusRef = useRef<string | null>(null);
    
    useEffect(() => {
        prevEnfinStatusRef.current = null;
    }, [opportunityID]);

    useEffect(() => {
        if (!changeOrderDetails || !opportunityDetails) return;
        
        const enfinDetail = changeOrderDetails.find((detail: any) => 
            detail?.Finance_Partner_Name === 'Enfin'
        );
        
        const currentStatus = enfinDetail?.application_Status || null;
        const prevStatus = prevEnfinStatusRef.current;
        
        const isNowApproved = currentStatus === 'CREDIT APPROVED';
        const wasApproved = prevStatus === 'CREDIT APPROVED';
        
        if (prevStatus !== null && currentStatus !== prevStatus) {
            if ((isNowApproved && !wasApproved) || (!isNowApproved && wasApproved)) {
                const salesforceId = opportunityDetails?.SalesForce_Id;
                if (salesforceId) {
                    opportunityInfoServices.getOpportunityDetail(String(salesforceId)).then((data) => {
                        if (data) useOneButtonStore.getState().setOpportunityDetails(data);
                    }).catch(e => console.error("Auto-refresh failed", e));
                }
            }
        }
        
        prevEnfinStatusRef.current = currentStatus;
    }, [changeOrderDetails, opportunityDetails, opportunityID]);

    const changeOrderPartnersText = useMemo(
        () => changeOrderPartnerLabels(changeOrderDetails).join(', '),
        [changeOrderDetails]
    );

    const showChangeOrderTag = (field: string): boolean => {
        if (!Array.isArray(changeOrderDetails) || changeOrderDetails.length === 0) return false;
        return changeOrderDetails.some((detail: any) => detail?.[field] && detail?.Is_Change_Order);
    };

    const openChangeOrderModal = (detail: any) => {
        setSelectedPartnerDetail(detail);
        setIsChangeOrderModalOpen(true);
    };

    const closeChangeOrderModal = () => {
        setIsChangeOrderModalOpen(false);
        setSelectedPartnerDetail(null);
    };

    const changeOrderModalTitle = selectedPartnerDetail
        ? `${selectedPartnerDetail.Finance_Partner_Name} - ${selectedPartnerDetail.Product_Name?.toUpperCase() ?? ''}`
        : 'Change Order Details';

    const showChecklistPopup = (type: 'change-order' | 'check-List') => {
        setChecklistModalType(type);
        setIsChecklistModalOpen(true);
    };

    const closeChecklistModal = () => {
        setIsChecklistModalOpen(false);
    };

    const groupedChecklistRows = useMemo(() => {
        const grouped: Record<string, { Changes: string; ChangeDate: string }[]> = {};
        if (Array.isArray(changeOrderDetails)){
            checkListDetails?.forEach((entry: any) => {
                const pageName = String(entry?.PageName ?? '');
                if (!pageName) return;
                if (!grouped[pageName]) grouped[pageName] = [];
                grouped[pageName].push({
                    Changes: String(entry?.Changes ?? ''),
                    ChangeDate: String(entry?.ChangeDate ?? ''),
                });
            });
        }
        return grouped;
    }, [checkListDetails]);

    const checklistTitle = checklistModalType === 'change-order' ? 'Confirm' : 'Checklist';

    const formatChecklistDate = (value: string) => {
        if (!value) return '';
        const d = dayjs(value);
        return d.isValid() ? d.format('MM/DD/YYYY hh:mm:ss A') : '';
    };

    const normalizeChecklistChange = (text: string) =>
        String(text)
            .replace(/changed from 'true' to 'false'/g, 'is disabled')
            .replace(/changed from 'false' to 'true'/g, 'is enabled');

    return (
        <>
            <footer
                className={`obd-dashboard-agent-footer obd-theme-${theme}`}
                role="region"
                aria-label="Live commerce guide"
            >
                <div className="obd-dashboard-agent-footer__inner">
                    {(changeOrderDetails && changeOrderDetails?.length > 0) && (
                        <div className="obd-dashboard-agent-footer__agent">
                            {changeOrderShow && (
                                <div className="ch-button co-button">
                                    <span className="sub-name">CO</span>
                                </div>
                            )}

                            <span>
                                {changeOrderDetails.map((detail: any, idx: number) => {
                                    if (detail.application_Status)
                                        return (
                                            <div
                                                className="partner-button clickable"
                                                key={`${detail.Finance_Partner_Name ?? 'partner'}-${detail.Product_Name ?? 'product'}-${idx}`}
                                                onClick={() => openChangeOrderModal(detail)}
                                                role="button"
                                                tabIndex={0}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter' || e.key === ' ') {
                                                        e.preventDefault();
                                                        openChangeOrderModal(detail);
                                                    }
                                                }}
                                            >
                                                <span className="partner-name">
                                                    {detail.Finance_Partner_Name} - {detail.Product_Name?.toUpperCase()}
                                                </span>
                                                <span className="partner-name status-indicator position-relative">
                                                    <span>{detail.application_Status}</span>
                                                    <i className="fa fa-check-circle success-badge ms-1" aria-hidden="true"></i>
                                                </span>
                                                {(detail.Is_Utility_Updated || detail.Is_System_Design_Updated || detail.Is_Quote_Updated) && detail.Is_Change_Order && (
                                                    <span className="tags position-relative">
                                                        {detail.Is_Utility_Updated && <span>Utility</span>}
                                                        {detail.Is_System_Design_Updated && <span>Design</span>}
                                                        {detail.Is_Quote_Updated && <span>Quote</span>}
                                                        {!detail.Is_Quote_Updated && (detail.Is_Utility_Updated || detail.Is_System_Design_Updated) && <span>New Quote is Required</span>}
                                                        <ExclamationCircleFilled className="warning-badge" aria-hidden />
                                                    </span>
                                                )}
                                            </div>
                                        );
                                    return '';
                                })}
                            </span>
                            { enableContractCancellationRequired && checkListDetails && checkListDetails.length > 0 && (

                                <div
                                    className="ch-button cancellation-btn ms-1"
                                    onClick={() => showChecklistPopup('check-List')}
                                    role="button"
                                    tabIndex={0}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                            e.preventDefault();
                                            showChecklistPopup('check-List');
                                        }
                                    }}
                                >
                                    <span className="sub-name">Contract Cancellation Required <i className="fa fa-light fa-info-circle ms-1"></i></span>
                                </div>
                            )}
                        </div>
                    )}
                {/* <div className="obd-dashboard-agent-footer__agent">
                    <Sparkle size={22} weight="duotone" className="obd-dashboard-agent-footer__icon" aria-hidden />
                    <div className="obd-dashboard-agent-footer__text-block">
                        <span className="obd-dashboard-agent-footer__label">Live guide</span>
                        <p className="obd-dashboard-agent-footer__message">{agentMessage}</p>
                    </div>
                    <Popover
                        trigger={['click']}
                        placement="top"
                        autoAdjustOverflow
                        destroyTooltipOnHide
                        getPopupContainer={(trigger) => trigger.ownerDocument.body}
                        overlayClassName="obd-dashboard-agent-footer__mobile-popover"
                        content={<span className="obd-dashboard-agent-footer__mobile-popover-text">{agentMessage}</span>}
                    >
                        <button
                            type="button"
                            className="obd-dashboard-agent-footer__info-btn"
                            aria-label="Show live guide message"
                            title="Show live guide message"
                        >
                            <Info size={14} weight="bold" aria-hidden />
                        </button>
                    </Popover>
                </div> */}
                <div
                    className="obd-dashboard-agent-footer__actions obd-metrics__action-group"
                    role="group"
                    aria-label="Commerce shortcuts"
                >
                        <DesignLossesSourceBadge
                            lossesSource={systemDesignLosses?.LossesSource}
                            className="obd-dashboard-agent-footer__losses-badge"
                        />
                        <button
                            type="button"
                            className={`obd-merged-system__chip-btn${!commerceReady ? ' is-disabled' : ''}`}
                            data-obd-guide="commerce-products"
                            aria-disabled={!commerceReady || undefined}
                            title={
                                commerceReady
                                    ? undefined
                                    : 'Complete required customer, utility, and design steps first'
                            }
                            onClick={() => openCommerceShortcut('products')}
                        >
                            <Cube size={16} weight="bold" aria-hidden />
                            <span>Products</span>
                        </button>
                        <button
                            type="button"
                            className={`obd-merged-system__chip-btn${!commerceReady ? ' is-disabled' : ''}`}
                            data-obd-guide="commerce-quotes"
                            aria-disabled={!commerceReady || undefined}
                            title={
                                commerceReady
                                    ? undefined
                                    : 'Complete required customer, utility, and design steps first'
                            }
                            onClick={() => openCommerceShortcut('quotes')}
                        >
                            <Scroll size={16} weight="bold" aria-hidden />
                            <span>Quotes</span>
                        </button>
                        {!hideAgreements && (
                            <button
                                type="button"
                                className={`obd-merged-system__chip-btn${!commerceReady ? ' is-disabled' : ''}`}
                                data-obd-guide="commerce-agreements"
                                aria-disabled={!commerceReady || undefined}
                                title={
                                    commerceReady
                                        ? undefined
                                        : 'Complete required customer, utility, and design steps first'
                                }
                                onClick={() => openCommerceShortcut('agreements')}
                            >
                                <Article size={16} weight="bold" aria-hidden />
                                <span>Agreements</span>
                            </button>
                        )}
                </div>
                </div>
            </footer>
            <DashboardModal
                open={isChangeOrderModalOpen}
                onClose={closeChangeOrderModal}
                title={changeOrderModalTitle}
                theme={theme}
                width={520}
                zIndex={12060}
                getContainer={() => document.body}
                className="obd-change-order-modal"
                rootClassName="obd-change-order-modal-root"
                footer={null}
            >
                {selectedPartnerDetail && (
                    <div className="modal-content">
                        <div className="info-section">
                            <div className="info-label">Application ID:</div>
                            <div className="info-value">{selectedPartnerDetail.Application_Id}</div>
                        </div>
                        {selectedPartnerDetail.Reference_Number && (
                            <div className="info-section">
                                <div className="info-label">Reference Number:</div>
                                <div className="info-value">{selectedPartnerDetail.Reference_Number}</div>
                            </div>
                        )}
                        {selectedPartnerDetail.is_Application_Approved && (
                            <div className="info-section">
                                <span className="status-label">Application Status:</span>
                                <span className="status-value approved">{selectedPartnerDetail.application_Status}</span>
                            </div>
                        )}
                        <div className="change-section">
                            <h3>Changes</h3>
                            <ul className="change-list">
                                {selectedPartnerDetail.Is_Utility_Updated && <li>Utility Changed</li>}
                                {selectedPartnerDetail.Is_System_Design_Updated && <li>Design Changed</li>}
                                {selectedPartnerDetail.Is_Quote_Updated && <li>Quote Changed</li>}
                                {(!selectedPartnerDetail.Is_Quote_Updated && selectedPartnerDetail.Is_Utility_Updated) ||
                                selectedPartnerDetail.Is_System_Design_Updated ? (
                                    <li>A New Quote Required</li>
                                ) : null}
                            </ul>
                        </div>
                    </div>
                )}
            </DashboardModal>
            <Modal
                open={isChecklistModalOpen}
                onCancel={closeChecklistModal}
                footer={null}
                destroyOnHidden
                centered
                maskClosable
                className="obd-checklist-modal"
                title={checklistTitle}
                width={710}
                styles={{ body: { maxHeight: '65vh', overflowY: 'auto' } }}
            >
                <div className="popup-content">
                    {checklistModalType === 'change-order' ? (
                        <>
                            {showChangeOrderTag('Is_Utility_Updated') && (
                                <div className="page-group"><strong>Utility Information Updated</strong></div>
                            )}
                            {showChangeOrderTag('Is_System_Design_Updated') && (
                                <div className="page-group"><strong>System Design Updated</strong></div>
                            )}
                            <div className="page-group text-warning">A new quote is required for this change</div>
                        </>
                    ) : (
                        Object.entries(groupedChecklistRows).map(([page, rows]) => (
                            <div className="page-group" key={page}>
                                <strong>{page}</strong>
                                <table>
                                    <thead>
                                        <tr>
                                            <th>Change</th>
                                            <th className="date-field-width">Date</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {rows.map((item, idx) => (
                                            <tr key={`${page}-${idx}`}>
                                                <td>{normalizeChecklistChange(item.Changes)}</td>
                                                <td>{formatChecklistDate(item.ChangeDate)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        ))
                    )}
                </div>
            </Modal>
        </>
    );
};

export default DashboardAgentFooter;
